import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Input } from '@/components/ui/input';
import { Search, ArrowRight } from 'lucide-react';
import { glossaryTerms, GlossaryTerm } from '@/components/glossary/glossaryData';

const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

const GlossaryEntry: React.FC<{ term: GlossaryTerm; allTerms: GlossaryTerm[] }> = ({ term, allTerms }) => {
  return (
    <article id={term.slug} className="scroll-mt-32 py-10 border-b border-border last:border-b-0">
      <h2 className="text-2xl md:text-3xl font-ibm-plex font-semibold text-foreground mb-4 tracking-tight">
        {term.term}
      </h2>

      {/* One-sentence definition */}
      <p className="text-lg text-foreground/90 font-medium leading-relaxed mb-6 border-l-4 border-primary pl-4 bg-muted/40 py-3 pr-4 rounded-r-md">
        {term.definition}
      </p>

      {/* Expanded explanation */}
      <div className="space-y-3 mb-6">
        {term.explanation.map((para, i) => (
          <p key={i} className="text-foreground/80 leading-relaxed font-montserrat">{para}</p>
        ))}
      </div>

      {/* Why it matters */}
      <div className="mb-6">
        <h3 className="text-lg font-ibm-plex font-medium text-foreground mb-3">Why It Matters</h3>
        <ul className="space-y-2">
          {term.whyItMatters.map((point, i) => (
            <li key={i} className="flex items-start gap-2 text-foreground/80 font-montserrat">
              <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
              <span>{point}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* NZ Example */}
      <div className="mb-6">
        <h3 className="text-lg font-ibm-plex font-medium text-foreground mb-3">New Zealand Example</h3>
        <p className="text-foreground/80 font-montserrat leading-relaxed bg-secondary/50 p-4 rounded-lg">{term.nzExample}</p>
      </div>

      {/* Advisory Context */}
      <div className="mb-6">
        <h3 className="text-lg font-ibm-plex font-medium text-foreground mb-3">Advisory Context</h3>
        <p className="text-foreground/80 font-montserrat leading-relaxed">{term.advisoryContext}</p>
      </div>

      {/* Related Terms */}
      <div>
        <h3 className="text-lg font-ibm-plex font-medium text-foreground mb-3">Related Terms</h3>
        <div className="flex flex-wrap gap-2">
          {term.relatedTerms.map((related) => {
            const linkedTerm = allTerms.find(t => t.term === related);
            if (!linkedTerm) return <span key={related} className="text-sm text-muted-foreground">{related}</span>;
            return (
              <a
                key={related}
                href={`#${linkedTerm.slug}`}
                className="text-sm px-3 py-1.5 rounded-full bg-muted text-foreground/80 hover:bg-primary hover:text-primary-foreground transition-colors font-montserrat"
              >
                {related}
              </a>
            );
          })}
        </div>
      </div>
    </article>
  );
};

const Glossary: React.FC = () => {
  const [search, setSearch] = useState('');
  const { hash } = useLocation();
  const navRef = useRef<HTMLDivElement>(null);

  // Scroll to anchor on load
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 100);
      }
    }
  }, [hash]);

  const filteredTerms = useMemo(() => {
    if (!search.trim()) return glossaryTerms;
    const q = search.toLowerCase();
    return glossaryTerms.filter(t =>
      t.term.toLowerCase().includes(q) ||
      t.definition.toLowerCase().includes(q)
    );
  }, [search]);

  const availableLetters = useMemo(() => {
    return new Set(filteredTerms.map(t => t.term[0].toUpperCase()));
  }, [filteredTerms]);

  // Group terms by first letter
  const grouped = useMemo(() => {
    const groups: Record<string, GlossaryTerm[]> = {};
    filteredTerms.forEach(t => {
      const letter = t.term[0].toUpperCase();
      if (!groups[letter]) groups[letter] = [];
      groups[letter].push(t);
    });
    return groups;
  }, [filteredTerms]);

  const scrollToLetter = (letter: string) => {
    const firstTerm = filteredTerms.find(t => t.term[0].toUpperCase() === letter);
    if (firstTerm) {
      const el = document.getElementById(firstTerm.slug);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // FAQ schema for definitions
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": glossaryTerms.map(t => ({
      "@type": "Question",
      "name": `What is ${t.term}?`,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": t.definition
      }
    }))
  };

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Think Tomorrow AI",
    "url": "https://thinktomorrow.ai",
    "description": "AI adoption advisory and workflow automation for New Zealand businesses."
  };

  return (
    <>
      <Helmet>
        <title>AI Adoption & Workflow Glossary | Think Tomorrow AI</title>
        <meta name="description" content="Definitions of key AI adoption, workflow automation, and operational improvement concepts for businesses. A structured reference for education and implementation." />
        <link rel="canonical" href="https://thinktomorrow.ai/glossary" />
        <meta property="og:title" content="AI Adoption & Workflow Glossary | Think Tomorrow AI" />
        <meta property="og:description" content="Practical definitions of AI readiness, workflow automation, AI governance, and more — designed for SMEs." />
        <meta property="og:url" content="https://thinktomorrow.ai/glossary" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "DefinedTermSet",
          "name": "AI Adoption & Workflow Glossary",
          "url": "https://thinktomorrow.ai/glossary",
          "hasDefinedTerm": glossaryTerms.map((t: any) => ({
            "@type": "DefinedTerm",
            "name": t.term,
            "description": t.definition,
            "url": `https://thinktomorrow.ai/glossary#${t.slug || String(t.term).toLowerCase().replace(/\s+/g, '-')}`
          }))
        })}</script>
      </Helmet>

      <Navbar />

      <main className="pt-16">
        {/* Header */}
        <header className="bg-gradient-to-r from-[hsl(var(--primary))] to-[hsl(220,60%,25%)] py-12 md:py-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-primary-foreground mb-4 font-space tracking-tight">
              AI Adoption & Workflow Glossary
            </h1>
            <p className="text-lg text-primary-foreground/80 max-w-3xl font-montserrat leading-relaxed">
              This glossary defines key concepts used in modern AI adoption, workflow automation, and operational improvement for businesses. Each entry provides a clear definition, practical context, and a New Zealand example to support education and implementation planning.
            </p>
          </div>
        </header>

        {/* Search + A-Z nav */}
        <div className="sticky top-16 z-40 bg-background border-b border-border shadow-sm">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search terms..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <nav ref={navRef} aria-label="Alphabetical navigation" className="flex flex-wrap gap-1">
              {alphabet.map(letter => (
                <button
                  key={letter}
                  onClick={() => scrollToLetter(letter)}
                  disabled={!availableLetters.has(letter)}
                  className={`w-8 h-8 text-xs font-medium rounded transition-colors ${
                    availableLetters.has(letter)
                      ? 'text-foreground hover:bg-primary hover:text-primary-foreground'
                      : 'text-muted-foreground/40 cursor-not-allowed'
                  }`}
                  aria-label={`Jump to terms starting with ${letter}`}
                >
                  {letter}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Glossary entries */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
          {filteredTerms.length === 0 ? (
            <p className="py-16 text-center text-muted-foreground font-montserrat">No terms match your search.</p>
          ) : (
            Object.keys(grouped).sort().map(letter => (
              <div key={letter}>
                {grouped[letter].map(term => (
                  <GlossaryEntry key={term.slug} term={term} allTerms={glossaryTerms} />
                ))}
              </div>
            ))
          )}
        </section>

        {/* CTA Section */}
        <section className="bg-muted/50 border-t border-border">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
            <h2 className="text-2xl md:text-3xl font-ibm-plex font-semibold text-foreground mb-4">
              Want to see how these technologies apply to real businesses?
            </h2>
            <p className="text-foreground/70 font-montserrat mb-8 max-w-2xl mx-auto">
              Explore our workflow automation playbooks and case studies to see how professional service firms are implementing these concepts.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/ai-automation-professional-services"
                className="inline-flex items-center justify-center px-6 py-3 rounded-md bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors"
              >
                Explore Workflow Automation <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
              <Link
                to="/case-studies/architect-workflow-intelligence"
                className="inline-flex items-center justify-center px-6 py-3 rounded-md border border-border text-foreground font-medium hover:bg-muted transition-colors"
              >
                View Case Studies <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default Glossary;
